import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../utils/firebase';

function ImageManager({ images = {}, onUpdate, onAutoSave, orgId, onSave, saving, savedMessage }) {
  const [uploading, setUploading]   = useState(null); // 'hero' | 'gallery' | null
  const [uploadError, setUploadError] = useState('');
  const [autoSaving, setAutoSaving]  = useState(false);

  const heroUrl = images.hero    || '';
  const gallery = images.gallery || [];

  const uploadFile = async (file, path) => {
    const ts       = Date.now();
    const safeName = `${ts}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
    const storageRef = ref(storage, `organizations/${orgId || 'default'}/${path}/${safeName}`);
    const snapshot   = await uploadBytes(storageRef, file, { contentType: file.type || 'image/jpeg' });
    return getDownloadURL(snapshot.ref);
  };

  // After a successful upload update parent state AND immediately persist to Firestore
  const persistImages = async (newImages) => {
    onUpdate(newImages);            // keep local state in sync
    if (onAutoSave) {
      setAutoSaving(true);
      try {
        await onAutoSave(newImages);
      } catch (err) {
        setUploadError('Image uploaded but failed to save. Please click Save Changes.');
        console.error('Auto-save failed:', err);
      } finally {
        setAutoSaving(false);
      }
    }
  };

  // ── Hero dropzone ────────────────────────────────────────────────────────────
  const { getRootProps: getHeroRootProps, getInputProps: getHeroInputProps, isDragActive: heroDrag } = useDropzone({
    onDrop: async (files) => {
      if (!files.length) return;
      setUploading('hero');
      setUploadError('');
      try {
        const url = await uploadFile(files[0], 'hero');
        await persistImages({ ...images, hero: url });
      } catch (err) {
        setUploadError('Failed to upload hero image. Please try again.');
        console.error(err);
      } finally {
        setUploading(null);
      }
    },
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] },
    maxFiles: 1,
  });

  // ── Gallery dropzone ─────────────────────────────────────────────────────────
  const { getRootProps: getGalleryRootProps, getInputProps: getGalleryInputProps, isDragActive: galleryDrag } = useDropzone({
    onDrop: async (files) => {
      if (!files.length) return;
      setUploading('gallery');
      setUploadError('');
      try {
        const urls      = await Promise.all(files.map(f => uploadFile(f, 'gallery')));
        const newImages = { ...images, gallery: [...gallery, ...urls] };
        await persistImages(newImages);
      } catch (err) {
        setUploadError('Failed to upload gallery images. Please try again.');
        console.error(err);
      } finally {
        setUploading(null);
      }
    },
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] },
    maxFiles: 10,
  });

  const removeHero = async () => {
    const newImages = { ...images, hero: '' };
    await persistImages(newImages);
  };

  const removeGalleryImage = async (index) => {
    const newImages = { ...images, gallery: gallery.filter((_, i) => i !== index) };
    await persistImages(newImages);
  };

  const isBusy = uploading !== null || autoSaving;

  return (
    <div className="bb-editor">
      <div className="bb-editor-header">
        <div>
          <h2>Image Management</h2>
          <p className="bb-editor-desc">Upload your hero banner and gallery images for the community portal.</p>
        </div>
        <div className="bb-editor-actions">
          {autoSaving && <span className="bb-save-msg">Saving images…</span>}
          {savedMessage && !autoSaving && (
            <span className={`bb-save-msg ${savedMessage.includes('Error') ? 'error' : ''}`}>{savedMessage}</span>
          )}
          <button className="bb-btn-primary" onClick={onSave} disabled={saving || isBusy}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {uploadError && <div className="bb-alert bb-alert--error">{uploadError}</div>}

      {/* ── Hero image ───────────────────────────────────────────────────────── */}
      <div className="bb-form-section">
        <h3 className="bb-section-title">Hero Banner <span className="bb-required">*</span></h3>
        <p className="bb-editor-desc">The main banner image shown at the top of your community portal.</p>
        <span className="bb-hint">Recommended: 1200×400px · JPG, PNG or WebP · Max 10 MB</span>

        {heroUrl ? (
          <div className="bb-image-preview-hero">
            <img src={heroUrl} alt="Hero banner" className="bb-hero-preview-img" />
            <div className="bb-image-preview-actions">
              <button type="button" className="bb-btn-danger-sm" onClick={removeHero} disabled={isBusy}>
                Remove Hero Image
              </button>
            </div>
          </div>
        ) : (
          <div
            {...getHeroRootProps()}
            className={`bb-dropzone bb-dropzone--hero ${heroDrag ? 'active' : ''} ${uploading === 'hero' ? 'uploading' : ''}`}
          >
            <input {...getHeroInputProps()} />
            {uploading === 'hero' ? (
              <div className="bb-upload-progress">
                <div className="bb-upload-spinner" />
                <p>Uploading &amp; saving…</p>
              </div>
            ) : heroDrag ? (
              <p>Drop your hero image here…</p>
            ) : (
              <>
                <div className="bb-dropzone-icon">🖼️</div>
                <p>Drag &amp; drop your hero banner, or <span className="bb-link">browse files</span></p>
                <span className="bb-hint">JPG, PNG, WebP · Max 10 MB · Recommended 1200×400px</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Gallery ──────────────────────────────────────────────────────────── */}
      <div className="bb-form-section">
        <h3 className="bb-section-title">Gallery Images</h3>
        <p className="bb-editor-desc">Photos showcasing your organisation's events, community, and activities.</p>

        {gallery.length > 0 && (
          <div className="bb-gallery-grid">
            {gallery.map((url, i) => (
              <div key={i} className="bb-gallery-item">
                <img src={url} alt={`Gallery ${i + 1}`} className="bb-gallery-img" />
                <button
                  type="button"
                  className="bb-gallery-remove"
                  onClick={() => removeGalleryImage(i)}
                  disabled={isBusy}
                  title="Remove image"
                >×</button>
              </div>
            ))}
          </div>
        )}

        <div
          {...getGalleryRootProps()}
          className={`bb-dropzone ${galleryDrag ? 'active' : ''} ${uploading === 'gallery' ? 'uploading' : ''}`}
        >
          <input {...getGalleryInputProps()} />
          {uploading === 'gallery' ? (
            <div className="bb-upload-progress">
              <div className="bb-upload-spinner" />
              <p>Uploading &amp; saving images…</p>
            </div>
          ) : galleryDrag ? (
            <p>Drop images here…</p>
          ) : (
            <>
              <div className="bb-dropzone-icon">📁</div>
              <p>Drag &amp; drop multiple images, or <span className="bb-link">browse files</span></p>
              <span className="bb-hint">Up to 10 images per upload · JPG, PNG, WebP · Max 10 MB each</span>
            </>
          )}
        </div>

        {gallery.length > 0 && (
          <p className="bb-hint">{gallery.length} image{gallery.length !== 1 ? 's' : ''} in gallery</p>
        )}
      </div>
    </div>
  );
}

export default ImageManager;
